import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Wallet } from 'lucide-react';
import { format } from 'date-fns';
import { useCurrency } from '../hooks/useCurrency';
import { formatApiError } from '../lib/apiError';
import { AdvanceFormDialog } from '../components/advances/AdvanceFormDialog';
import { AdvanceCardList } from '../components/advances/AdvanceCardList';
import { AdvanceTable } from '../components/advances/AdvanceTable';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = { worker_id: '', amount: '', reason: '' };

const Advances = () => {
  const [workers, setWorkers] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showDeducted, setShowDeducted] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { format: fmt, symbol } = useCurrency();

  const fetchData = useCallback(async () => {
    try {
      const [workersRes, advancesRes] = await Promise.all([
        axios.get(`${API_URL}/api/workers?active_only=true`),
        axios.get(`${API_URL}/api/advances`)
      ]);
      setWorkers(workersRes.data);
      setAdvances(advancesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.worker_id || !formData.amount) {
      toast.error('Worker and amount are required');
      return;
    }

    if (parseFloat(formData.amount) <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/advances`, {
        worker_id: formData.worker_id,
        amount: parseFloat(formData.amount),
        reason: formData.reason,
        date: format(new Date(), 'yyyy-MM-dd')
      });
      toast.success('Advance recorded successfully');
      setIsDialogOpen(false);
      setFormData(emptyForm);
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to record advance'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMarkDeducted = async (advanceId) => {
    try {
      await axios.put(`${API_URL}/api/advances/${advanceId}/mark-deducted`);
      toast.success('Advance marked as deducted');
      fetchData();
    } catch (error) {
      toast.error('Failed to update advance');
    }
  };

  const handleDelete = async (advanceId) => {
    if (!window.confirm('Are you sure you want to delete this advance record?')) return;

    try {
      await axios.delete(`${API_URL}/api/advances/${advanceId}`);
      toast.success('Advance deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete advance');
    }
  };

  const filteredAdvances = showDeducted
    ? advances
    : advances.filter(a => !a.is_deducted);

  const totalPending = advances
    .filter(a => !a.is_deducted)
    .reduce((sum, a) => sum + a.amount, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="advances-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2E4632]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="advances-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1
            className="text-3xl md:text-4xl font-bold text-[#1C1C1E] tracking-tight"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Advance Payments
          </h1>
          <p className="text-[#5C5C5C] mt-1">
            Track advance payments to workers
          </p>
        </div>

        <AdvanceFormDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          workers={workers}
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          symbol={symbol}
        />
      </div>

      <Card className="bg-[#D97706] text-white rounded-lg shadow-sm">
        <CardContent className="p-5 md:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm font-medium uppercase tracking-wide">
                Total Pending Advances
              </p>
              <p className="text-4xl font-bold mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {fmt(totalPending)}
              </p>
            </div>
            <div className="p-4 bg-white/20 rounded-full">
              <Wallet className="w-8 h-8" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border border-[#D1D1CD] rounded-lg shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Label className="text-sm font-medium text-[#5C5C5C]">Show deducted:</Label>
            <Button
              variant={showDeducted ? "default" : "outline"}
              size="sm"
              data-testid="toggle-deducted-btn"
              onClick={() => setShowDeducted(!showDeducted)}
              className={showDeducted ? "bg-[#2E4632] hover:bg-[#1F3122]" : "border-[#D1D1CD]"}
            >
              {showDeducted ? 'Showing All' : 'Showing Pending Only'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <AdvanceCardList
        advances={filteredAdvances}
        fmt={fmt}
        onMarkDeducted={handleMarkDeducted}
        onDelete={handleDelete}
      />

      <AdvanceTable
        advances={filteredAdvances}
        fmt={fmt}
        onMarkDeducted={handleMarkDeducted}
        onDelete={handleDelete}
      />
    </div>
  );
};

export default Advances;
